import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AUTH_TOKEN_KEY,
  clearAccountScopedClientState,
  clearLocalAuthState,
  getApiUrl,
  isPublicAuthRequestUrl,
  normalizeLoginEmail,
} from '../utils/authUtils';
import {
  AUTH_BOOTSTRAP_STATES,
  assertSafePreviewApiTarget,
  detectContradictorySessionScope,
  homePathForScopedSession,
  isScopedSelfRepairSession,
} from '../utils/sessionScope';
import {
  clearDevAuthSession,
  isDevAuthToken,
  isFrontendDevAuthBypassEnabled,
  logDevAuthWarning,
  persistDevAuthSession,
  readDevAuthSession,
  tryDevAuthLogin,
} from '../utils/devAuth';
import {
  AUTH_REFRESHED_EVENT,
  AUTH_SESSION_EXPIRED_EVENT,
  clearAuthOnRefreshFailure,
  getStoredAccessToken,
  refreshAuthSession,
  shouldAttemptAuthRefresh,
} from '../services/authSession';

const API = getApiUrl();

export const AUTH_STATES = {
  UNAUTHENTICATED: 'unauthenticated',
  ACCOUNT_SELECTION_REQUIRED: 'account_selection_required',
  AUTHENTICATED: 'authenticated',
};

const isPreviewOrDevDiagnostics = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  return (
    process.env.NODE_ENV === 'development'
    || process.env.REACT_APP_VERCEL_ENV === 'preview'
    || host.endsWith('.vercel.app')
  );
};

const logAuthDiag = (event, payload = {}) => {
  if (!isPreviewOrDevDiagnostics()) return;
  try {
    // Never log tokens, cookies, JWTs, or selection secrets.
    console.info('[AUTH_DIAG]', event, {
      frontendOrigin: typeof window !== 'undefined' ? window.location.origin : null,
      apiOrigin: API,
      ...payload,
    });
  } catch {
    /* ignore */
  }
};

// Global axios interceptor — credentials + Authorization (never on public auth routes)
axios.interceptors.request.use(
  (config) => {
    config.withCredentials = true;

    const url = String(config.url || '');
    if (isPublicAuthRequestUrl(url)) {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    const token = getStoredAccessToken();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

let consecutiveAuthFailures = 0;

axios.interceptors.response.use(
  (response) => {
    consecutiveAuthFailures = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = String(originalRequest.url || '');

    if (
      status === 401
      && shouldAttemptAuthRefresh(url, { retried: Boolean(originalRequest._authRetried) })
    ) {
      originalRequest._authRetried = true;
      const hadStoredToken = Boolean(getStoredAccessToken());

      // Cookie-only sessions authenticate via credentials/include. Do not invoke
      // /auth/refresh solely because localStorage has no JWT — endpoints must
      // accept the HttpOnly cookie. Refresh remains for Bearer-token sessions.
      if (!hadStoredToken) {
        return Promise.reject(error);
      }

      const refreshedToken = await refreshAuthSession();
      if (refreshedToken) {
        consecutiveAuthFailures = 0;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
        originalRequest.withCredentials = true;
        return axios(originalRequest);
      }
      clearAuthOnRefreshFailure();
    }

    if (status === 401) {
      consecutiveAuthFailures += 1;
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[AUTH] Auth failure ${consecutiveAuthFailures}`);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapState, setBootstrapState] = useState(AUTH_BOOTSTRAP_STATES.PENDING);
  const [token, setToken] = useState(localStorage.getItem(AUTH_TOKEN_KEY));
  const [sessionReady, setSessionReady] = useState(false);
  const [authState, setAuthState] = useState(AUTH_STATES.UNAUTHENTICATED);
  const [selectionChallenge, setSelectionChallenge] = useState(null);

  const checkAuth = useCallback(async () => {
    setBootstrapState(AUTH_BOOTSTRAP_STATES.PENDING);
    setSessionReady(false);

    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      setBootstrapState(AUTH_BOOTSTRAP_STATES.PENDING);
      return;
    }

    const previewGuard = assertSafePreviewApiTarget();
    if (!previewGuard.ok && process.env.NODE_ENV !== 'test') {
      console.error('[AUTH]', previewGuard.reason);
    }

    const applyAuthUser = (userData, authToken = null) => {
      if (userData && (userData.user_id || userData.email)) {
        if (
          userData.requires_account_selection
          || userData.pending_account_selection
          || userData.account_selection_required
        ) {
          setUser({
            user_id: userData.user_id,
            email: userData.email,
            name: userData.name,
            display_name: userData.display_name || userData.name,
            requires_account_selection: true,
            pending_account_selection: true,
          });
          if (authToken) setToken(authToken);
          setSessionReady(false);
          setAuthState(AUTH_STATES.ACCOUNT_SELECTION_REQUIRED);
          setBootstrapState(AUTH_BOOTSTRAP_STATES.READY);
          logAuthDiag('bootstrap_account_selection_required', {
            authState: AUTH_STATES.ACCOUNT_SELECTION_REQUIRED,
            requires_account_selection: true,
            contexts: Array.isArray(userData.accounts) ? userData.accounts.length : null,
          });
          return true;
        }

        // Never keep a contradictory agency shell when server scope is self-repair.
        const scoped = { ...userData };
        if (isScopedSelfRepairSession(scoped)) {
          scoped.portal = scoped.portal || 'self_repair';
          scoped.account_type = scoped.account_type || 'self_repair';
          scoped.agency_id = null;
          if (scoped.active_account) {
            scoped.active_account = {
              ...scoped.active_account,
              agency_id: null,
            };
          }
        }

        const contradiction = detectContradictorySessionScope(scoped);
        if (contradiction) {
          logAuthDiag('contradictory_session_fail_closed', {
            reason: contradiction.reason,
            portal_type: contradiction.portal,
            account_type: contradiction.accountType,
            role: contradiction.role,
            agency_id: contradiction.agency_id,
          });
          clearLocalAuthState();
          clearAccountScopedClientState();
          setToken(null);
          setUser(null);
          setSessionReady(false);
          setAuthState(AUTH_STATES.UNAUTHENTICATED);
          setSelectionChallenge(null);
          setBootstrapState(AUTH_BOOTSTRAP_STATES.UNAUTHENTICATED);
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.assign('/choose-account');
          }
          return false;
        }

        setUser(scoped);
        if (authToken) setToken(authToken);
        setSessionReady(true);
        setAuthState(AUTH_STATES.AUTHENTICATED);
        setSelectionChallenge(null);
        setBootstrapState(AUTH_BOOTSTRAP_STATES.READY);
        logAuthDiag('bootstrap_authenticated', {
          authState: AUTH_STATES.AUTHENTICATED,
          portal_type: scoped.portal || scoped.portal_type || null,
          role: scoped.effective_role || scoped.role || null,
          agency_id: scoped.agency_id || null,
          redirect_path: scoped.redirect_to || null,
        });
        return true;
      }
      clearLocalAuthState();
      clearAccountScopedClientState();
      setToken(null);
      setUser(null);
      setSessionReady(false);
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      setSelectionChallenge(null);
      setBootstrapState(AUTH_BOOTSTRAP_STATES.UNAUTHENTICATED);
      return false;
    };

    try {
      if (isFrontendDevAuthBypassEnabled()) {
        const devSession = readDevAuthSession();
        if (devSession) {
          localStorage.setItem(AUTH_TOKEN_KEY, devSession.token);
          applyAuthUser(devSession.user, devSession.token);
          setLoading(false);
          return;
        }
      }

      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      if (storedToken && isDevAuthToken(storedToken)) {
        clearLocalAuthState();
        clearDevAuthSession();
        setToken(null);
        setUser(null);
        setSessionReady(false);
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
        setSelectionChallenge(null);
        setBootstrapState(AUTH_BOOTSTRAP_STATES.UNAUTHENTICATED);
        setLoading(false);
        return;
      }

      const storedTokenForApi = localStorage.getItem(AUTH_TOKEN_KEY);
      if (storedTokenForApi) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedTokenForApi}` },
          withCredentials: true,
        });
        applyAuthUser(response.data, storedTokenForApi);
      } else {
        // Cookie-session auth: /auth/me can succeed without a stored bearer token.
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        const cookieToken =
          response.data?.token
          || response.data?.access_token
          || localStorage.getItem(AUTH_TOKEN_KEY);
        if (cookieToken) {
          localStorage.setItem(AUTH_TOKEN_KEY, cookieToken);
        }
        applyAuthUser(response.data, cookieToken || null);
      }
    } catch (error) {
      if (isFrontendDevAuthBypassEnabled()) {
        const devSession = readDevAuthSession();
        if (devSession) {
          applyAuthUser(devSession.user, devSession.token);
          setLoading(false);
          return;
        }
      }
      // CORS / network / 403: never keep a guessed shell from a bare token.
      // Stale localStorage role/agency must not render Agency Workspace.
      console.warn('[AUTH] Auth bootstrap failed — clearing cached auth state:', error.message);
      clearLocalAuthState();
      clearAccountScopedClientState();
      setToken(null);
      setUser(null);
      setSessionReady(false);
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      setSelectionChallenge(null);
      setBootstrapState(
        error.response?.status === 401
          ? AUTH_BOOTSTRAP_STATES.UNAUTHENTICATED
          : AUTH_BOOTSTRAP_STATES.ERROR
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    logDevAuthWarning();
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onRefreshed = (event) => {
      const nextToken = event?.detail?.token || localStorage.getItem(AUTH_TOKEN_KEY);
      if (nextToken) setToken(nextToken);
    };
    const onExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(AUTH_REFRESHED_EVENT, onRefreshed);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(AUTH_REFRESHED_EVENT, onRefreshed);
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onExpired);
    };
  }, []);

  const login = async (email, password) => {
    clearLocalAuthState();
    clearAccountScopedClientState();
    clearDevAuthSession();
    setToken(null);
    setUser(null);
    setSessionReady(false);
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    setSelectionChallenge(null);

    const devSession = tryDevAuthLogin(email, password);
    if (devSession) {
      persistDevAuthSession(devSession);
      setToken(devSession.token);
      setUser(devSession.user);
      setSessionReady(true);
      setAuthState(AUTH_STATES.AUTHENTICATED);
      consecutiveAuthFailures = 0;
      return devSession.user;
    }

    const response = await axios.post(
      `${API}/auth/login`,
      { email: normalizeLoginEmail(email), password },
      { withCredentials: true }
    );
    const data = response.data || {};
    const { token: newToken, user: userData } = data;

    if (data.requires_account_selection || data.account_selection_required) {
      // Identity is authenticated; final workspace not chosen yet.
      // Do not mark sessionReady — no portal shell until selection completes.
      if (newToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, newToken);
        setToken(newToken);
      }
      const challengeAccounts =
        data.selection_challenge?.accounts
        || data.accounts
        || [];
      const challengeId = data.challenge_id || data.selection_token || data.selection_challenge?.challenge_id;
      setSelectionChallenge({
        challenge_id: challengeId,
        expires_at: data.selection_challenge?.expires_at || null,
        accounts: challengeAccounts,
      });
      if (userData) {
        setUser({
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          display_name: userData.display_name || userData.name,
          requires_account_selection: true,
          pending_account_selection: true,
        });
      }
      setSessionReady(false);
      setAuthState(AUTH_STATES.ACCOUNT_SELECTION_REQUIRED);
      setBootstrapState(AUTH_BOOTSTRAP_STATES.READY);
      consecutiveAuthFailures = 0;
      logAuthDiag('login_account_selection_required', {
        authState: AUTH_STATES.ACCOUNT_SELECTION_REQUIRED,
        requires_account_selection: true,
        contexts: challengeAccounts.length,
      });
      return {
        ...userData,
        requires_account_selection: true,
        account_selection_required: true,
        selection_token: challengeId,
        challenge_id: challengeId,
        accounts: challengeAccounts,
        selection_challenge: data.selection_challenge || {
          challenge_id: challengeId,
          accounts: challengeAccounts,
        },
        redirect_to: null,
      };
    }

    localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    setToken(newToken);
    const enrichedUser = {
      ...userData,
      ...(data.active_account ? { active_account: data.active_account } : {}),
      ...(data.selected_account ? { selected_account: data.selected_account } : {}),
      ...(data.portal ? { portal: data.portal } : {}),
      ...(data.effective_role ? { effective_role: data.effective_role } : {}),
      ...(data.agency_id ? { agency_id: data.agency_id } : {}),
      ...(data.membership_id ? { membership_id: data.membership_id } : {}),
      ...(data.redirect_to ? { redirect_to: data.redirect_to } : {}),
      accounts: data.accounts || [],
    };
    // Prefer membership role / agency from active account over stale users.role.
    if (data.active_account?.role) {
      enrichedUser.role = data.active_account.effective_role || data.active_account.role;
    }
    {
      const acctType = String(
        data.active_account?.type
        || data.active_account?.account_type
        || data.account_type
        || ''
      ).toLowerCase();
      const portal = String(data.active_account?.portal || data.portal || '').toLowerCase();
      const isSelfRepair = acctType === 'self_repair' || portal === 'self_repair';
      const isPlatform = acctType === 'platform' || portal === 'platform';
      if (isSelfRepair || isPlatform) {
        enrichedUser.agency_id = null;
        enrichedUser.account_type = isSelfRepair ? 'self_repair' : 'platform';
        if (isSelfRepair) {
          enrichedUser.portal = 'self_repair';
          enrichedUser.portal_type = 'self_serve_portal';
        }
      } else if (data.active_account?.agency_id) {
        enrichedUser.agency_id = data.active_account.agency_id;
      } else if (
        data.active_account?.account_id
        && (acctType === 'agency' || acctType === 'subaccount' || acctType === 'funding_hq'
          || portal === 'agency' || portal === 'agency_staff' || portal === 'funding_hq')
      ) {
        enrichedUser.agency_id = data.active_account.account_id;
      }
    }
    if (data.portal) {
      enrichedUser.portal_type =
        ['agency', 'agency_staff', 'platform', 'funding_hq'].includes(data.portal)
          ? 'staff_portal'
          : data.portal === 'self_repair'
            ? 'self_serve_portal'
            : 'managed_client_portal';
      enrichedUser.is_staff = ['agency', 'agency_staff', 'platform', 'funding_hq'].includes(data.portal);
      enrichedUser.is_client = ['client', 'self_repair'].includes(data.portal);
    }
    setToken(newToken);
    setUser(enrichedUser);
    setSessionReady(true);
    setAuthState(AUTH_STATES.AUTHENTICATED);
    setSelectionChallenge(null);
    setBootstrapState(AUTH_BOOTSTRAP_STATES.READY);
    consecutiveAuthFailures = 0;
    logAuthDiag('login_authenticated', {
      authState: AUTH_STATES.AUTHENTICATED,
      requires_account_selection: false,
      contexts: Array.isArray(data.accounts) ? data.accounts.length : 1,
      portal_type: enrichedUser.portal || enrichedUser.portal_type || null,
      role: enrichedUser.effective_role || enrichedUser.role || null,
      agency_id: enrichedUser.agency_id || null,
      redirect_path: data.redirect_to || data.redirect_path || null,
    });
    return {
      ...enrichedUser,
      requires_account_selection: false,
      redirect_to: data.redirect_to || null,
      active_account: data.active_account || null,
      accounts: data.accounts || [],
    };
  };

  const selectAccount = async (accountId, selectionToken) => {
    const response = await axios.post(
      `${API}/auth/select-account`,
      {
        account_id: accountId,
        account_context_id: accountId,
        selection_token: selectionToken,
        challenge_id: selectionToken,
      },
      { withCredentials: true }
    );
    const data = response.data || {};
    if (data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      setToken(data.token);
    }
    clearAccountScopedClientState();
    setSelectionChallenge(null);
    // Refresh canonical session before routing.
    try {
      const me = await axios.get(`${API}/auth/me`, {
        headers: data.token ? { Authorization: `Bearer ${data.token}` } : undefined,
        withCredentials: true,
      });
      const meUser = me.data || {};
      setUser(meUser);
      setSessionReady(true);
      setAuthState(AUTH_STATES.AUTHENTICATED);
      setBootstrapState(AUTH_BOOTSTRAP_STATES.READY);
      logAuthDiag('account_selected', {
        authState: AUTH_STATES.AUTHENTICATED,
        selected_account_context_id:
          data.session?.account_context_id || data.active_account?.account_id || null,
        portal_type: meUser.portal || data.session?.portal_type || null,
        role: meUser.effective_role || data.session?.role || null,
        agency_id: meUser.agency_id || data.session?.agency_id || null,
        redirect_path: data.redirect_to || data.redirect_path || null,
      });
    } catch (err) {
      if (data.active_account) {
        setUser((prev) => ({
          ...(prev || {}),
          ...data.active_account,
          active_account: data.active_account,
          portal: data.active_account.portal || data.active_account.portal_type,
          role: data.active_account.role,
          agency_id: data.session?.agency_id ?? prev?.agency_id,
          redirect_to: data.redirect_to,
        }));
        setSessionReady(true);
        setAuthState(AUTH_STATES.AUTHENTICATED);
      }
    }
    consecutiveAuthFailures = 0;
    return data;
  };

  const switchAccount = async (accountId) => {
    const response = await axios.post(
      `${API}/auth/switch-account`,
      { account_id: accountId },
      { withCredentials: true }
    );
    const data = response.data || {};
    if (data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, data.token);
      setToken(data.token);
    }
    clearAccountScopedClientState();
    // Update identity shell; consumers should hard-navigate to redirect_to so
    // entitlements, branding, flags, and nav reload from the new account.
    if (data.active_account) {
      const acctType = String(
        data.active_account.type || data.active_account.account_type || ''
      ).toLowerCase();
      const portal = String(data.active_account.portal || '').toLowerCase();
      const isSelfRepair = acctType === 'self_repair' || portal === 'self_repair';
      const isPlatform = acctType === 'platform' || portal === 'platform';
      const agencyId = isSelfRepair || isPlatform
        ? null
        : (data.active_account.agency_id
          || (
            ['agency', 'subaccount', 'funding_hq'].includes(acctType)
            || ['agency', 'agency_staff', 'funding_hq'].includes(portal)
              ? data.active_account.account_id
              : null
          ));
      setUser((prev) => ({
        ...(prev || {}),
        agency_id: agencyId,
        role: data.active_account.effective_role || data.active_account.role || prev?.role,
        effective_role: data.active_account.effective_role || data.active_account.role || prev?.effective_role,
        account_type: acctType || prev?.account_type,
        portal: portal || (isSelfRepair ? 'self_repair' : prev?.portal),
        portal_type: isSelfRepair
          ? 'self_serve_portal'
          : isPlatform
            ? 'staff_portal'
            : prev?.portal_type,
        active_account: {
          ...data.active_account,
          agency_id: agencyId,
          type: acctType || data.active_account.type,
          account_type: acctType || data.active_account.account_type,
        },
      }));
    }
    consecutiveAuthFailures = 0;
    return data;
  };

  const listAccounts = async () => {
    // Canonical path without trailing slash — avoid FastAPI redirect_slashes 307
    // which can drop CORS credentials on credentialed GETs.
    const base = String(API || '').replace(/\/$/, '');
    const response = await axios.get(`${base}/auth/accounts`, { withCredentials: true });
    return response.data || {};
  };

  const signup = async (email, password, name) => {
    clearLocalAuthState();
    setToken(null);
    setUser(null);

    const response = await axios.post(
      `${API}/auth/signup`,
      { email: normalizeLoginEmail(email), password, name },
      { withCredentials: true }
    );
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem(AUTH_TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!isDevAuthToken(token)) {
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    clearLocalAuthState();
    clearAccountScopedClientState();
    clearDevAuthSession();
    try {
      localStorage.removeItem('fundelite_selected_account_id');
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    setSessionReady(false);
    setAuthState(AUTH_STATES.UNAUTHENTICATED);
    setSelectionChallenge(null);
    setBootstrapState(AUTH_BOOTSTRAP_STATES.UNAUTHENTICATED);
  };

  const updateUser = (updatedData) => {
    setUser((prev) => ({ ...prev, ...updatedData }));
  };

  const getAuthHeaders = useCallback(() => {
    const activeToken = token || localStorage.getItem(AUTH_TOKEN_KEY);
    if (activeToken) {
      return { Authorization: `Bearer ${activeToken}` };
    }
    return {};
  }, [token]);

  const handleScopedAuthFailure = useCallback((error) => {
    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || error?.message || '');
    if (status === 403 && /account context|selection required|platform owner/i.test(detail)) {
      clearAccountScopedClientState();
      const path = homePathForScopedSession(user) || '/choose-account';
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign(path === '/unauthorized' ? '/choose-account' : path);
      }
    }
  }, [user]);

  const awaitingAccountSelection = authState === AUTH_STATES.ACCOUNT_SELECTION_REQUIRED;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        bootstrapState,
        sessionReady,
        authState,
        selectionChallenge,
        awaitingAccountSelection,
        login,
        signup,
        logout,
        selectAccount,
        switchAccount,
        listAccounts,
        processOAuthSession,
        updateUser,
        getAuthHeaders,
        checkAuth,
        handleScopedAuthFailure,
        // Fully authenticated = scoped portal session only (not awaiting chooser).
        isAuthenticated: !!user && sessionReady && authState === AUTH_STATES.AUTHENTICATED,
        hasIdentity: !!user || !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
